import orderBy from "lodash/orderBy";

import { NO_REFUND_POLICY, formatCancellationPenalties } from "@holibob-packages/cancellation-policy";

import { ProductDataCancellationPolicy } from "../../../apiHooks";
import { ProductDataCancellationPolicyPenaltyListNode } from "../../../apiHooks/useProduct";
import { Typography } from "../../../components";
import { BulletedList } from "../../../components/BulletedList";
import { useLanguage, useNextTranslation } from "../../../hooks";
import { ListItem } from "../ListItem";
import { ProductSectionTitle } from "../ProductSectionTitle";
import { ProductSectionWrapper, ProductSectionWrapperProps } from "./ProductSectionWrapper";

type ProductCancellationPenaltySectionProps = Omit<ProductSectionWrapperProps, "children"> & {
    cancellationPolicy: ProductDataCancellationPolicy;
};
export const ProductCancellationPenaltySection = ({
    cancellationPolicy,
    ...props
}: ProductCancellationPenaltySectionProps) => {
    const [t] = useNextTranslation("product");

    return (
        <ProductSectionWrapper {...props} data-testid="product-cancellation-policy-section">
            <ProductSectionTitle>{t("label.cancellationPolicy")}</ProductSectionTitle>
            <ProductCancellationPenalties penaltyList={cancellationPolicy.penaltyList.nodes} />
        </ProductSectionWrapper>
    );
};

type ProductCancellationPenaltiesProps = {
    penaltyList: ProductDataCancellationPolicyPenaltyListNode[];
};
export const ProductCancellationPenalties = (props: ProductCancellationPenaltiesProps) => {
    const { penaltyList } = props;
    const [t] = useNextTranslation("product");
    const language = useLanguage();

    if (penaltyList.length === 0) {
        const cancellationPolicyList = formatCancellationPenalties([NO_REFUND_POLICY], language.code);
        return (
            <Typography color="subtle" size="large">
                {cancellationPolicyList[0].formattedText || t("cancellationPolicy.noRefund")}
            </Typography>
        );
    }

    if (penaltyList.length === 1) {
        return (
            <Typography color="subtle" size="large">
                {penaltyList[0].formattedText}
            </Typography>
        );
    }

    return (
        <BulletedList data-testid="product-cancellation-penalties">
            {orderBy(penaltyList, "ordinalPosition").map((penalty, idx) => (
                <ListItem key={idx}>
                    <Typography color="subtle" size="large">
                        {penalty.formattedText}
                    </Typography>
                </ListItem>
            ))}
        </BulletedList>
    );
};
